import { useState, useEffect } from 'react';
import { useNavigate }         from 'react-router-dom';
import { Capacitor }           from '@capacitor/core';
import { stripeTerminal }      from '../native/stripeTerminal.js';
import { useTerminalStore }    from '../store/terminalStore.js';
import { useSessionStore }     from '../store/sessionStore.js';
import { getMpDevices }        from '../api/pos.api.js';
import styles from './ReaderPickerScreen.module.css';

const isNative = Capacitor.isNativePlatform();

const CONNECTION_LABEL = {
  bluetooth:    'Bluetooth',
  internet:     'Internet / Red local',
  local_mobile: 'Tap to Pay (iPhone)',
};

// ─── Stripe Terminal UI ───────────────────────────────────────────────────────

function StripeReaderPicker({ terminal }) {
  const status     = useTerminalStore((s) => s.status);
  const reader     = useTerminalStore((s) => s.reader);
  const setStatus  = useTerminalStore((s) => s.setStatus);
  const setReader  = useTerminalStore((s) => s.setReader);
  const setError   = useTerminalStore((s) => s.setError);
  const handleDisconnect = useTerminalStore((s) => s.handleDisconnect);

  const [readers,    setReaders]    = useState([]);
  const [connecting, setConnecting] = useState(null);
  const [error,      setLocalError] = useState(null);
  const navigate = useNavigate();

  const connectionType = terminal?.connection_type ?? 'bluetooth';
  const locationId     = terminal?.stripe_location_id ?? undefined;

  useEffect(() => {
    if (!isNative) setLocalError('El lector físico solo funciona en el dispositivo (iOS/Android).');
  }, []);

  const handleDiscover = async () => {
    setLocalError(null);
    setReaders([]);
    setStatus('discovering');
    try {
      const found = await stripeTerminal.discoverReaders({ connectionType, locationId });
      setReaders(found);
      if (found.length === 0) setLocalError('No se encontraron lectores. Verifica que esté encendido y cerca.');
    } catch (err) {
      setLocalError(err.message ?? 'Error al buscar lectores');
      setError(err.message ?? 'Error al buscar lectores');
    } finally {
      setStatus('disconnected');
    }
  };

  const handleConnect = async (r) => {
    setLocalError(null);
    setConnecting(r.serialNumber);
    setStatus('connecting');
    try {
      await stripeTerminal.connectReader({ reader: r, locationId });
      setReader(r);
      navigate(-1);
    } catch (err) {
      setLocalError(err.message ?? 'No se pudo conectar. Intenta de nuevo.');
      setStatus('disconnected');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnectReader = async () => {
    await stripeTerminal.disconnect();
    handleDisconnect();
    setReaders([]);
    setLocalError(null);
  };

  return (
    <>
      {reader && status === 'connected' ? (
        <div className={styles.connectedCard}>
          <div className={styles.connectedDot} />
          <div className={styles.connectedInfo}>
            <p className={styles.connectedName}>{reader.label ?? reader.serialNumber}</p>
            <p className={styles.connectedSub}>
              {CONNECTION_LABEL[connectionType]} · {reader.serialNumber}
            </p>
          </div>
          <button className={styles.disconnectBtn} onClick={handleDisconnectReader}>
            Desconectar
          </button>
        </div>
      ) : (
        <div className={styles.noReader}>
          <p className={styles.noReaderText}>Sin lector Stripe conectado</p>
          <p className={styles.noReaderSub}>Tipo: {CONNECTION_LABEL[connectionType] ?? connectionType}</p>
        </div>
      )}

      {error && <p className={styles.error}>{error}</p>}
      {!isNative && (
        <p className={styles.webNote}>
          El lector de tarjetas solo está disponible en el dispositivo físico (iPad/Android).
          En el navegador puedes usar efectivo o transferencia.
        </p>
      )}

      {isNative && status !== 'connected' && (
        <>
          <button
            className="btn btn--primary btn--full"
            onClick={handleDiscover}
            disabled={status === 'discovering' || status === 'connecting'}
            style={{ marginTop: 8 }}
          >
            {status === 'discovering' ? (
              <><span className="spinner" style={{ width: 16, height: 16, marginRight: 8 }} /> Buscando…</>
            ) : 'Buscar lectores'}
          </button>

          {readers.length > 0 && (
            <div className={styles.readerList}>
              <p className={styles.listLabel}>Lectores encontrados</p>
              {readers.map((r) => (
                <button
                  key={r.serialNumber}
                  className={styles.readerRow}
                  onClick={() => handleConnect(r)}
                  disabled={!!connecting}
                >
                  <div className={styles.readerInfo}>
                    <p className={styles.readerName}>{r.label ?? r.serialNumber}</p>
                    <p className={styles.readerSub}>{r.serialNumber} · {r.deviceType ?? connectionType}</p>
                  </div>
                  {connecting === r.serialNumber ? (
                    <span className="spinner" style={{ width: 18, height: 18, flexShrink: 0 }} />
                  ) : (
                    <span className={styles.connectArrow}>Conectar →</span>
                  )}
                </button>
              ))}
            </div>
          )}
        </>
      )}
    </>
  );
}

// ─── Mercado Pago Point UI ────────────────────────────────────────────────────

function MpDevicePicker({ terminal }) {
  const [devices,  setDevices]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  const assignedDeviceId = terminal?.mp_device_id ?? null;

  useEffect(() => {
    setLoading(true);
    getMpDevices()
      .then((d) => setDevices(d))
      .catch((err) => setError(err.response?.data?.error ?? 'Error al obtener terminales'))
      .finally(() => setLoading(false));
  }, []);

  return (
    <>
      <div className={styles.noReader}>
        <p className={styles.noReaderText}>Terminales Mercado Pago Point</p>
        <p className={styles.noReaderSub}>
          La terminal activa para esta caja es la configurada en el backend.
          {assignedDeviceId && <> ID asignado: <code>{assignedDeviceId}</code></>}
        </p>
      </div>

      {error && <p className={styles.error}>{error}</p>}

      {loading ? (
        <div style={{ textAlign: 'center', padding: 24 }}>
          <span className="spinner" />
        </div>
      ) : devices.length === 0 ? (
        <p className={styles.webNote}>
          No se encontraron terminales Point en tu cuenta de Mercado Pago.
          Asegúrate de que estén en modo PDV (ve a Ajustes → POS / Terminal).
        </p>
      ) : (
        <div className={styles.readerList}>
          <p className={styles.listLabel}>Terminales disponibles</p>
          {devices.map((d) => (
            <div
              key={d.id}
              className={styles.readerRow}
              style={{ cursor: 'default', opacity: assignedDeviceId === d.id ? 1 : 0.6 }}
            >
              <div className={styles.readerInfo}>
                <p className={styles.readerName}>{d.name ?? d.id}</p>
                <p className={styles.readerSub}>
                  {d.operating_mode} · {d.id}
                  {assignedDeviceId === d.id && ' · Asignado a esta caja'}
                </p>
              </div>
              {assignedDeviceId === d.id && (
                <span className={styles.connectedDot} style={{ flexShrink: 0 }} />
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

// ─── Screen principal ─────────────────────────────────────────────────────────

export default function ReaderPickerScreen() {
  const navigate = useNavigate();
  const terminal = useSessionStore((s) => s.terminal);
  const provider = terminal?.terminal_provider ?? 'stripe';

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
          <h1 className={styles.title}>
            {provider === 'mercadopago' ? 'Terminal Mercado Pago' : 'Lector de tarjetas'}
          </h1>
        </div>

        {provider === 'mercadopago' ? (
          <MpDevicePicker terminal={terminal} />
        ) : (
          <StripeReaderPicker terminal={terminal} />
        )}
      </div>
    </div>
  );
}
